<template>
  <div class="private-chat">
    <!-- MODERATOR VIEW: collapsible sidebar + thread panel -->
    <template v-if="isModerator">
      <div
        class="private-chat__mod-view"
        :class="{ 'private-chat__mod-view--sidebar-hidden': sidebarCollapsed }"
      >
        <!-- Sidebar -->
        <div class="private-chat__sidebar">
          <div class="private-chat__sidebar-header">
            <span class="private-chat__sidebar-title">Chats</span>
            <button
              class="private-chat__sidebar-collapse"
              title="Hide sidebar"
              @click="sidebarCollapsed = true"
            >
              <v-icon size="16">
                mdi-chevron-left
              </v-icon>
            </button>
          </div>

          <div class="private-chat__sidebar-list">
            <div
              v-if="sortedThreadEntries.length === 0"
              class="private-chat__sidebar-empty"
            >
              No conversations yet
            </div>
            <button
              v-for="[userId, thread] in sortedThreadEntries"
              :key="userId"
              class="private-chat__sidebar-item"
              :class="{
                'private-chat__sidebar-item--active': activeUserId === userId,
                'private-chat__sidebar-item--unread': unreadPrivateUsers[userId] > 0,
              }"
              @click="selectUser(userId)"
            >
              <span class="private-chat__sidebar-avatar">
                {{ getInitialFromThread(thread) }}
              </span>
              <span class="private-chat__sidebar-info">
                <span class="private-chat__sidebar-name">{{ getUserName(thread) }}</span>
                <span class="private-chat__sidebar-preview">{{ getLastMessage(thread) }}</span>
              </span>
              <span
                v-if="unreadPrivateUsers[userId] > 0"
                class="private-chat__sidebar-badge"
              >
                {{ unreadPrivateUsers[userId] }}
              </span>
            </button>
          </div>
        </div>

        <!-- Thread panel -->
        <div class="private-chat__thread-panel">
          <!-- Expand sidebar button (shown when collapsed) -->
          <button
            v-if="sidebarCollapsed"
            class="private-chat__sidebar-expand"
            title="Show sidebar"
            @click="sidebarCollapsed = false"
          >
            <v-icon size="16">
              mdi-menu
            </v-icon>
            <span>Chats</span>
            <span
              v-if="totalUnread > 0"
              class="private-chat__sidebar-badge private-chat__sidebar-badge--inline"
            >
              {{ totalUnread }}
            </span>
          </button>

          <template v-if="activeUserId">
            <ChatThread
              :messages="privateThreads[activeUserId] || []"
              :current-user-id="currentUserId"
            />
            <div class="private-chat__input-row">
              <v-text-field
                v-model="newMessage"
                placeholder="Reply..."
                outlined
                dense
                hide-details
                :disabled="sending"
                class="private-chat__input"
                @keydown.enter.prevent="sendMessage"
              />
              <v-btn
                icon
                color="primary"
                small
                class="private-chat__send-btn"
                :disabled="!newMessage.trim() || sending"
                @click="sendMessage"
              >
                <v-icon
                  small
                  color="white"
                >
                  mdi-send
                </v-icon>
              </v-btn>
            </div>
          </template>
          <div
            v-else
            class="private-chat__select-hint"
          >
            <v-icon
              color="grey lighten-1"
              size="32"
            >
              mdi-message-text-outline
            </v-icon>
            <span>{{ hasThreads ? 'Select a conversation' : 'No private messages yet.' }}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- USER VIEW: single thread with the moderator -->
    <template v-else>
      <div class="private-chat__user-view">
        <ChatThread
          :messages="privateMessages"
          :current-user-id="currentUserId"
        />
        <div class="private-chat__input-row">
          <v-text-field
            v-model="newMessage"
            placeholder="Message the moderator..."
            outlined
            dense
            hide-details
            :disabled="sending"
            class="private-chat__input"
            @keydown.enter.prevent="sendMessage"
          />
          <v-btn
            icon
            color="primary"
            small
            class="private-chat__send-btn"
            :disabled="!newMessage.trim() || sending"
            @click="sendMessage"
          >
            <v-icon
              small
              color="white"
            >
              mdi-send
            </v-icon>
          </v-btn>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import ChatThread from './ChatThread.vue';

export default {
  name: 'PrivateChatTab',

  components: { ChatThread },

  props: {
    isModerator: {
      type: Boolean,
      default: false,
    },
    currentUserId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      newMessage: '',
      sending: false,
      activeUserId: null,
      sidebarCollapsed: false,
    };
  },

  computed: {
    ...mapGetters('webinarChat', ['privateMessages', 'privateThreads', 'unreadPrivateUsers']),

    hasThreads() {
      return Object.keys(this.privateThreads).length > 0;
    },

    // Sort thread entries so users with unread messages appear first
    sortedThreadEntries() {
      return Object.entries(this.privateThreads).sort(([aId], [bId]) => {
        const aUnread = this.unreadPrivateUsers[aId] || 0;
        const bUnread = this.unreadPrivateUsers[bId] || 0;
        return bUnread - aUnread;
      });
    },

    // Total unread across all private threads — shown on expand button
    totalUnread() {
      return Object.values(this.unreadPrivateUsers).reduce((sum, n) => sum + (n || 0), 0);
    },
  },

  methods: {
    ...mapActions('webinarChat', ['sendPrivateMessage']),
    ...mapMutations('webinarChat', ['setActivePrivateUser']),

    selectUser(userId) {
      this.activeUserId = userId;
      this.setActivePrivateUser(userId);
    },

    getUserName(thread) {
      const first = thread && thread[0];
      if (!first) return 'Unknown';
      return first.is_from_moderator
        ? first.recipient?.name || 'User'
        : first.sender?.name || 'User';
    },

    getLastMessage(thread) {
      const last = thread && thread[thread.length - 1];
      return last ? last.content.slice(0, 38) : '';
    },

    getInitialFromThread(thread) {
      const name = this.getUserName(thread);
      return name.charAt(0).toUpperCase();
    },

    async sendMessage() {
      if (!this.newMessage.trim() || this.sending) return;
      this.sending = true;
      try {
        const recipientId = this.isModerator ? Number(this.activeUserId) : undefined;
        await this.sendPrivateMessage({
          content: this.newMessage.trim(),
          recipientId,
        });
        this.newMessage = '';
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.private-chat {
  display: flex;
  height: 100%;

  // ── Moderator layout ────────────────────────────────────────────────────
  &__mod-view {
    flex: 1;
    display: flex;
    flex-direction: row;
    overflow: hidden;
  }

  // ── Sidebar ─────────────────────────────────────────────────────────────
  &__sidebar {
    width: 170px;
    min-width: 170px;
    display: flex;
    flex-direction: column;
    border-right: 1px solid rgba(0, 0, 0, 0.08);
    background: #fafafa;
    overflow: hidden;
    transition: width 0.2s ease, min-width 0.2s ease, opacity 0.2s ease;

    .private-chat__mod-view--sidebar-hidden & {
      width: 0;
      min-width: 0;
      opacity: 0;
      pointer-events: none;
    }
  }

  &__sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 8px 6px 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.07);
    flex-shrink: 0;
  }

  &__sidebar-title {
    font-size: 11px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.45);
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  &__sidebar-collapse {
    width: 22px;
    height: 22px;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(0, 0, 0, 0.4);
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.06);
      color: rgba(0, 0, 0, 0.7);
    }
  }

  &__sidebar-list {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;

    // thin scrollbar
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.15) transparent;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.15);
      border-radius: 4px;
    }
  }

  &__sidebar-empty {
    padding: 16px 10px;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.35);
    text-align: center;
  }

  &__sidebar-item {
    display: flex;
    align-items: center;
    gap: 7px;
    width: 100%;
    padding: 7px 8px 7px 8px;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
    transition: background 0.12s ease;
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);

    &:hover {
      background: rgba(244, 119, 33, 0.05);
    }

    &--active {
      background: #fef1e8;

      .private-chat__sidebar-avatar {
        background: #f47721;
        color: #fff;
      }

      .private-chat__sidebar-name {
        color: #f47721;
      }
    }

    &--unread {
      .private-chat__sidebar-name {
        font-weight: 700;
        color: rgba(0, 0, 0, 0.87);
      }

      .private-chat__sidebar-preview {
        color: rgba(0, 0, 0, 0.6);
        font-weight: 500;
      }
    }
  }

  &__sidebar-avatar {
    width: 28px;
    height: 28px;
    min-width: 28px;
    border-radius: 50%;
    background: #e0e0e0;
    color: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    transition: background 0.15s ease, color 0.15s ease;
  }

  &__sidebar-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__sidebar-name {
    font-size: 11px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.75);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: color 0.15s ease;
  }

  &__sidebar-preview {
    font-size: 10px;
    color: rgba(0, 0, 0, 0.4);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__sidebar-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 8px;
    background: #f47721;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    flex-shrink: 0;

    &--inline {
      margin-left: 4px;
    }
  }

  // ── Thread panel ─────────────────────────────────────────────────────────
  &__thread-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-width: 0;
  }

  &__sidebar-expand {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px 10px;
    border: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    background: #fafafa;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.55);
    transition: background 0.15s ease, color 0.15s ease;
    flex-shrink: 0;

    &:hover {
      background: rgba(244, 119, 33, 0.05);
      color: rgba(0, 0, 0, 0.75);
    }
  }

  // ── User view ────────────────────────────────────────────────────────────
  &__user-view {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__select-hint {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex: 1;
    color: rgba(0, 0, 0, 0.35);
    font-size: 12px;
  }

  // ── Input row ────────────────────────────────────────────────────────────
  &__input-row {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    background: #fafafa;
    flex-shrink: 0;
  }

  &__input {
    ::v-deep .v-input__slot {
      border-radius: 20px !important;
      min-height: 36px !important;
      padding: 0 14px !important;
      background: #fff !important;
      border: 1.5px solid rgba(0, 0, 0, 0.12) !important;
      transition: border-color 0.2s ease;

      &:hover {
        border-color: rgba(244, 119, 33, 0.4) !important;
      }
    }

    ::v-deep .v-input__slot fieldset {
      display: none;
    }

    ::v-deep .v-text-field__slot input {
      font-size: 13px;
      padding: 6px 0;
    }

    ::v-deep .v-input--is-focused .v-input__slot {
      border-color: #f47721 !important;
      box-shadow: 0 0 0 1px rgba(244, 119, 33, 0.15);
    }
  }

  &__send-btn {
    background: #f47721 !important;
    color: #fff !important;
    width: 34px;
    height: 34px;

    &.v-btn--disabled {
      background: #e0e0e0 !important;
      color: rgba(0, 0, 0, 0.26) !important;
    }
  }
}
</style>
